/**
 * `pinned` (product#116): the app's verdict on a colour the partner pins.
 * Mirrors `PinVerdict` in popin-rn `design/ds-sync/engine/pinned.ts` (read at
 * 68879ba0); it arrives on the preview's v2 ack (see preview-channel). The
 * refusal rule lives in the app — the portal reads it, never recomputes it.
 */
export type PinVerdict =
  | { pin: "primary" | "accent"; placed: true; value: string }
  | { pin: "primary" | "accent"; placed: false; reason: string };

const COLOUR_KEYS = ["background", "surface", "primary", "accent", "text", "header"] as const;
type ColourKey = (typeof COLOUR_KEYS)[number];

/** The app's answer, and the six colours of the draft it judged. */
export interface PinVerdictReport {
  colors: Record<ColourKey, string>;
  verdicts: PinVerdict[];
}

/**
 * The verdicts that hold for these colours: the report's, when it judged exactly
 * this draft; otherwise none (the app hasn't answered for these colours yet), so
 * a verdict never shows for, or clears, a colour it didn't judge.
 */
export function matchingVerdicts(
  report: PinVerdictReport | null | undefined,
  colors: Partial<Record<ColourKey, string | null>>,
): PinVerdict[] {
  return judges(report, colors) ? report.verdicts : [];
}

/** Whether the report judged exactly these six colours. */
export function judges(
  report: PinVerdictReport | null | undefined,
  colors: Partial<Record<ColourKey, string | null>>,
): report is PinVerdictReport {
  return !!report && COLOUR_KEYS.every((k) => report.colors[k] === colors[k]);
}

/** Same colours and same verdicts: a repeat ack that changes nothing. */
export function sameReport(a: PinVerdictReport | null, b: PinVerdictReport | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return judges(a, b.colors) && JSON.stringify(a.verdicts) === JSON.stringify(b.verdicts);
}
